import type { MetricEntry } from "./types";
import { recordedAt, entryDate } from "./history";

export type Account = { id: string; name: string; balance: number };
export type AccountEvent = { id: string; account_id: string; name: string; date: string; amount: number; balance: number; kind: "deposit" | "withdrawal" | "balance" | "payment" };

export function currentSnapshot<T extends { recorded_at: string; created_at?: string; details?: any }>(rows: T[]): T | null {
  // Aggregate weekly check-ins are not replacements for saved account records.
  const detailed = rows.filter((row) => Array.isArray(row.details?.debts) || Array.isArray(row.details?.accounts));
  const ordered = [...(detailed.length ? detailed : rows)].sort((a, b) => (a.created_at ?? a.recorded_at).localeCompare(b.created_at ?? b.recorded_at));
  return ordered[ordered.length - 1] ?? null;
}

export function applyAccountAmount<T extends Account>(accounts: T[], id: string, amount: number, kind: AccountEvent["kind"], date: string) {
  recordedAt(date);
  if (!Number.isFinite(amount) || amount < 0 || (kind !== "balance" && amount === 0)) throw new Error("Enter a valid amount");
  const account = accounts.find((a) => a.id === id);
  if (!account) throw new Error("Choose an account");
  const balance = Math.round((kind === "balance" ? amount : account.balance + (kind === "deposit" ? amount : -amount)) * 100) / 100;
  if (balance < 0) throw new Error("Amount exceeds the account balance");
  return { accounts: accounts.map((a) => a.id === id ? { ...a, balance } : a), event: { id: crypto.randomUUID(), account_id: id, name: account.name, date, amount, balance, kind } as AccountEvent };
}

export function accountHistory(rows: MetricEntry[], field: "debts" | "accounts", account: Account) {
  const events = new Map<string, AccountEvent>();
  for (const row of rows) {
    const accounts = (row.details?.[field] ?? []) as Account[];
    const saved = accounts.find((a) => a.id === account.id);
    const logs = (row.details?.account_events ?? []) as AccountEvent[];
    for (const event of logs) if (event.account_id === account.id) events.set(event.id, event);
    if (field === "debts") for (const [index, payment] of ((row.details?.payments ?? []) as { account_id?: string; debt: string; date: string; amount: number; balance?: number }[]).entries()) {
      const matches = payment.account_id ? payment.account_id === account.id : accounts.filter((a) => a.name === payment.debt).length === 1 && saved?.name === payment.debt;
      if (matches && payment.balance != null && !logs.some((e) => e.account_id === account.id && e.kind === "payment" && e.date === payment.date && e.amount === payment.amount)) {
        const id = `legacy:${payment.account_id ?? payment.debt}:${payment.date}:${payment.amount}:${index}`;
        events.set(id, { id, account_id: account.id, name: payment.debt, date: payment.date, amount: payment.amount, balance: payment.balance, kind: "payment" });
      }
    }
    if (saved && !logs.some((e) => e.account_id === saved.id && e.date === entryDate(new Date(row.recorded_at)))) {
      events.set(row.id, { id: row.id, account_id: saved.id, name: saved.name, balance: saved.balance, amount: saved.balance, kind: "balance", date: row.recorded_at });
    }
  }
  return [...events.values()].sort((a, b) => b.date.localeCompare(a.date));
}

export function savingsCash(details: Record<string, any>) {
  if (details.financial_accounts_version === 2) return Number(details.savings_cash ?? 0);
  return Number(details.trad_cash ?? 0) + Number(details.trad_mm ?? 0) + ((details.accounts ?? []) as (Account & { account: string })[]).filter((a) => a.account === "trad").reduce((sum, a) => sum + a.balance, 0);
}

export function goalProgress(saved: number, target: number) {
  return target > 0 ? Math.min(100, Math.max(0, saved / target * 100)) : 0;
}

export function financialDraftKey(userId: string | undefined, kind: string) {
  return `decorum-financial-draft:${userId ?? "signed-out"}:${kind}`;
}

export function reconcileDraft<T>(state: { value: T; dirty: boolean; source: string }, source: string, value: T) {
  return state.dirty || state.source === source ? state : { value, dirty: false, source };
}